import React, { useState } from "react";
import "./ContactUs.css";
import Swal from "sweetalert2";

// =====================================================
// API CONFIG
// =====================================================
const API_URL = process.env.REACT_APP_API_URL;

const CONTACT_ENDPOINT = `${API_URL}contact-us`;

// =====================================================
// INITIAL FORM DATA
// =====================================================
const initialFormData = {
  full_name: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

// =====================================================
// COMPONENT
// =====================================================
const ContactUs = () => {
  const [formData, setFormData] =
    useState(initialFormData);

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [submitted, setSubmitted] =
    useState(false);

  const [loading, setLoading] =
    useState(false);

  const [apiError, setApiError] =
    useState("");

  // =====================================================
  // VALIDATION
  // =====================================================
  const validateField = (name, value) => {
    const val =
      typeof value === "string"
        ? value.trim()
        : value;

    switch (name) {
      case "full_name": {
        if (!val) {
          return "Full name is required.";
        }

        if (val.length < 3) {
          return "Name must be at least 3 characters.";
        }

        if (val.length > 60) {
          return "Name must be less than 60 characters.";
        }

        if (!/^[a-zA-Z\s.'-]+$/.test(val)) {
          return "Name can only contain letters, spaces, and . ' -";
        }

        return "";
      }

      case "email": {
        if (!val) {
          return "Email is required.";
        }

        const emailRegex =
          /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

        if (!emailRegex.test(val)) {
          return "Enter a valid email address.";
        }

        return "";
      }

      case "phone": {
        if (!val) {
          return "Phone number is required.";
        }

        const phoneRegex =
          /^\+?[0-9]{10}$/;

        if (!phoneRegex.test(val)) {
          return "Enter a valid phone number (10 digits).";
        }

        return "";
      }

      case "subject": {
        if (!val) {
          return "Subject is required.";
        }

        if (val.length < 3) {
          return "Subject must be at least 3 characters.";
        }

        if (val.length > 120) {
          return "Subject must be less than 120 characters.";
        }

        return "";
      }

      case "message": {
        if (!val) {
          return "Message is required.";
        }

        if (val.length < 10) {
          return "Message must be at least 10 characters.";
        }

        if (val.length > 1000) {
          return "Message must be less than 1000 characters.";
        }

        return "";
      }

      default:
        return "";
    }
  };

  // =====================================================
  // VALIDATE ALL
  // =====================================================
  const validateAll = (data) => {
    const newErrors = {};

    Object.keys(data).forEach((key) => {
      const error = validateField(
        key,
        data[key]
      );

      if (error) {
        newErrors[key] = error;
      }
    });

    return newErrors;
  };

  // =====================================================
  // HANDLE CHANGE
  // =====================================================
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Live validation after touched
    if (touched[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: validateField(
          name,
          value
        ),
      }));
    }

    // Clear API error
    if (apiError) {
      setApiError("");
    }
  };

  // =====================================================
  // HANDLE BLUR
  // =====================================================
  const handleBlur = (e) => {
    const { name, value } = e.target;

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: validateField(
        name,
        value
      ),
    }));
  };

  // =====================================================
  // HANDLE SUBMIT
  // =====================================================
  const handleSubmit = async (e) => {
    e.preventDefault();

    setApiError("");
    setSubmitted(false);

    // =================================================
    // MARK ALL FIELDS TOUCHED
    // =================================================
    const allTouched =
      Object.keys(formData).reduce(
        (acc, key) => {
          acc[key] = true;
          return acc;
        },
        {}
      );

    setTouched(allTouched);

    // =================================================
    // VALIDATE FORM
    // =================================================
    const validationErrors =
      validateAll(formData);

    setErrors(validationErrors);

    // =================================================
    // VALIDATION ERROR
    // =================================================
    if (
      Object.keys(validationErrors)
        .length > 0
    ) {
      const firstError =
        Object.keys(validationErrors)[0];

      const el =
        document.querySelector(
          `[name="${firstError}"]`
        );

      if (el) {
        el.focus();
      }

      Swal.fire({
        icon: "warning",
        title: "Please Check Your Form",
        text:
          "Please fill all required fields correctly.",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
      });

      return;
    }

    // =================================================
    // START LOADING
    // =================================================
    setLoading(true);

    try {
      // =================================================
      // CONTACT API
      // =================================================
      const response = await fetch(
        CONTACT_ENDPOINT,
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",

            Accept: "application/json",
          },

          body: JSON.stringify({
            full_name:
              formData.full_name.trim(),

            email:
              formData.email.trim(),

            phone:
              formData.phone.trim(),

            subject:
              formData.subject.trim(),

            message:
              formData.message.trim(),
          }),
        }
      );

      // =================================================
      // PARSE RESPONSE
      // =================================================
      const data =
        await response
          .json()
          .catch(() => ({}));

      console.log(
        "Contact API Response:",
        data
      );

      // =================================================
      // API ERROR
      // =================================================
      if (!response.ok) {
        throw new Error(
          data?.message ||
            data?.error ||
            `Request failed with status ${response.status}`
        );
      }

      // =================================================
      // SUCCESS
      // =================================================
      console.log(
        "Contact Form Submitted:",
        data
      );

      // Reset form
      setFormData(
        initialFormData
      );

      setErrors({});
      setTouched({});
      setApiError("");
      setSubmitted(true);

      // =================================================
      // SUCCESS SWEETALERT
      // =================================================
      await Swal.fire({
        icon: "success",
        title: "Message Sent!",
        text:
          data?.message ||
          "Your message has been submitted successfully. We will contact you soon.",
        confirmButtonText: "OK",
        confirmButtonColor:
          "#2563eb",
      });

      // Hide success message
      setTimeout(() => {
        setSubmitted(false);
      }, 5000);

    } catch (error) {
      // =================================================
      // ERROR
      // =================================================
      console.error(
        "Contact Form Error:",
        error
      );

      const errorMessage =
        error?.message ||
        "Something went wrong. Please try again.";

      setApiError(errorMessage);

      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: errorMessage,
        confirmButtonText: "OK",
        confirmButtonColor:
          "#dc2626",
      });

    } finally {
      // =================================================
      // STOP LOADING
      // =================================================
      setLoading(false);
    }
  };

  // =====================================================
  // CONTACT INFORMATION
  // =====================================================
  const contacts = [
    {
      icon: "✉",
      label: "Email",
      value: "support@jomedtuon.com",
    },
    {
      icon: "☎",
      label: "Phone",
      value: "+91 98765 43210",
    },
    {
      icon: "⌖",
      label: "Location",
      value: "India",
    },
    {
      icon: "◷",
      label: "Working Hours",
      value: "Mon – Sat, 9 AM – 6 PM",
    },
  ];

  // =====================================================
  // FIELD CLASS
  // =====================================================
  const fieldClass = (name) =>
    `form-field${
      errors[name] && touched[name]
        ? " has-error"
        : ""
    }`;

  // =====================================================
  // JSX
  // =====================================================
  return (
    <div className="contact-page">

      {/* =================================================
          HERO
      ================================================= */}
      <section className="contact-hero">

        <div className="contact-badge">
          <span className="badge-dot"></span>
          CONTACT & SUPPORT
        </div>

        <h1 className="contact-hero-title">
          We're Here to{" "}
          <span>Help You</span>
        </h1>

        <p className="contact-hero-description">
          Whether you have a question,
          need technical assistance, or
          simply want to share your
          thoughts, our team is always
          ready to listen.
        </p>

      </section>

      {/* =================================================
          MAIN
      ================================================= */}
      <section className="contact-wrapper">

        <div className="contact-card">

          {/* =================================================
              LEFT
          ================================================= */}
          <div className="contact-left">

            <div className="left-glow glow-one"></div>

            <div className="left-glow glow-two"></div>

            <div className="contact-left-content">

              <div className="left-badge">
                GET IN TOUCH
              </div>

              <h2>
                Let's build something
                <span>
                  {" "}great together.
                </span>
              </h2>

              <p className="left-description">
                Have questions or feedback?
                Reach out through any of the
                channels below or send us a
                message using the form.
              </p>

              <div className="contact-list">

                {contacts.map(
                  (item, index) => (
                    <div
                      className="contact-item"
                      key={index}
                    >

                      <div className="contact-icon">
                        {item.icon}
                      </div>

                      <div className="contact-info">

                        <p className="contact-label">
                          {item.label}
                        </p>

                        <p className="contact-value">
                          {item.value}
                        </p>

                      </div>

                    </div>
                  )
                )}

              </div>

            </div>

          </div>

          {/* =================================================
              RIGHT
          ================================================= */}
          <div className="contact-right">

            <div className="form-header">

              <div className="form-mini-badge">
                ✦ MESSAGE US
              </div>

              <h2>
                Send us a message
              </h2>

              <p>
                Complete the form below
                and our team will respond
                as quickly as possible.
              </p>

            </div>

            {/* =================================================
                FORM
            ================================================= */}
            <form
              onSubmit={handleSubmit}
              noValidate
            >

              <div className="contact-form-grid">

                {/* FULL NAME */}
                <div
                  className={fieldClass(
                    "full_name"
                  )}
                >

                  <label>
                    FULL NAME
                  </label>

                  <input
                    type="text"
                    name="full_name"
                    value={
                      formData.full_name
                    }
                    onChange={
                      handleChange
                    }
                    onBlur={
                      handleBlur
                    }
                    placeholder="John Doe"
                    maxLength={60}
                    disabled={loading}
                  />

                  {touched.full_name &&
                    errors.full_name && (
                      <span className="field-error">
                        {
                          errors.full_name
                        }
                      </span>
                    )}

                </div>

                {/* EMAIL */}
                <div
                  className={fieldClass(
                    "email"
                  )}
                >

                  <label>
                    EMAIL ADDRESS
                  </label>

                  <input
                    type="email"
                    name="email"
                    value={
                      formData.email
                    }
                    onChange={
                      handleChange
                    }
                    onBlur={
                      handleBlur
                    }
                    placeholder="john@example.com"
                    maxLength={100}
                    disabled={loading}
                  />

                  {touched.email &&
                    errors.email && (
                      <span className="field-error">
                        {errors.email}
                      </span>
                    )}

                </div>

                {/* PHONE */}
                <div
                  className={fieldClass(
                    "phone"
                  )}
                >

                  <label>
                    PHONE NUMBER
                  </label>

                  <input
                    type="tel"
                    name="phone"
                    value={
                      formData.phone
                    }
                    onChange={
                      handleChange
                    }
                    onBlur={
                      handleBlur
                    }
                    placeholder="9876543210"
                    maxLength={10}
                    disabled={loading}
                  />

                  {touched.phone &&
                    errors.phone && (
                      <span className="field-error">
                        {errors.phone}
                      </span>
                    )}

                </div>

                {/* SUBJECT */}
                <div
                  className={fieldClass(
                    "subject"
                  )}
                >

                  <label>
                    SUBJECT
                  </label>

                  <input
                    type="text"
                    name="subject"
                    value={
                      formData.subject
                    }
                    onChange={
                      handleChange
                    }
                    onBlur={
                      handleBlur
                    }
                    placeholder="How can we help you?"
                    maxLength={120}
                    disabled={loading}
                  />

                  {touched.subject &&
                    errors.subject && (
                      <span className="field-error">
                        {
                          errors.subject
                        }
                      </span>
                    )}

                </div>

                {/* MESSAGE */}
                <div
                  className={`${fieldClass(
                    "message"
                  )} full`}
                >

                  <label>
                    YOUR MESSAGE
                  </label>

                  <textarea
                    name="message"
                    value={
                      formData.message
                    }
                    onChange={
                      handleChange
                    }
                    onBlur={
                      handleBlur
                    }
                    placeholder="Tell us how we can help..."
                    rows={5}
                    maxLength={1000}
                    disabled={loading}
                  />

                  <div className="field-meta">

                    {touched.message &&
                    errors.message ? (
                      <span className="field-error">
                        {
                          errors.message
                        }
                      </span>
                    ) : (
                      <span />
                    )}

                    <span className="char-count">
                      {
                        formData.message
                          .length
                      }
                      /1000
                    </span>

                  </div>

                </div>

                {/* BUTTON */}
                <div className="submit-wrapper">

                  <button
                    type="submit"
                    className="contact-submit"
                    disabled={loading}
                  >

                    <span>
                      {loading
                        ? "Sending..."
                        : "Send Message"}
                    </span>

                    <span className="arrow">
                      →
                    </span>

                  </button>

                </div>

              </div>

            </form>

            {/* SUCCESS MESSAGE */}
            {submitted && (
              <div className="success-message">
                <span>✓</span>

                Message sent successfully!
                We'll get back to you soon.
              </div>
            )}

            {/* API ERROR */}
            {apiError && (
              <div className="error-message">
                <span>✕</span>

                {apiError}
              </div>
            )}

          </div>

        </div>

      </section>

    </div>
  );
};

export default ContactUs;